import { DEFAULT_SETTINGS } from "../prefs";
import type { VoiceSettings } from "../types";
import { isV3 } from "./ModelPicker";

interface SliderProps {
  label: string;
  hint: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
}

function Slider({ label, hint, min, max, step, value, onChange }: SliderProps) {
  return (
    <div className="slider">
      <label>
        <span>{label}</span>
        <span className="slider-val">{value.toFixed(2)}</span>
      </label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(parseFloat(event.target.value))}
      />
      <div className="hint">{hint}</div>
    </div>
  );
}

interface Props {
  modelId: string;
  settings: VoiceSettings;
  onChange: (settings: VoiceSettings) => void;
}

/* The composer's `tuning` chip. The sliders are the most fiddled-with control
   on the page and the least often *looked* at: worth a click, not a column. */
export function VoiceSettingsForm({ modelId, settings, onChange }: Props) {
  const v3 = isV3(modelId);
  const patch = (part: Partial<VoiceSettings>) => onChange({ ...settings, ...part });

  return (
    <div className="picker">
      <div className="picker-head">
        <span className="field-label">Voice settings</span>
        <span className="spacer"></span>
        <button className="link" onClick={() => onChange(DEFAULT_SETTINGS)}>
          Reset
        </button>
      </div>

      <Slider
        label="Stability"
        hint="Lower is more expressive, higher is more consistent."
        min={0}
        max={1}
        step={v3 ? 0.5 : 0.05}
        value={settings.stability}
        onChange={(stability) => patch({ stability })}
      />

      {!v3 && (
        <>
          <Slider
            label="Similarity"
            hint="How closely it matches the original voice."
            min={0}
            max={1}
            step={0.05}
            value={settings.similarity_boost}
            onChange={(similarity_boost) => patch({ similarity_boost })}
          />
          <Slider
            label="Style exaggeration"
            hint="Exaggerates delivery. Adds latency."
            min={0}
            max={1}
            step={0.05}
            value={settings.style}
            onChange={(style) => patch({ style })}
          />
          <Slider
            label="Speed"
            hint="0.7 to 1.2"
            min={0.7}
            max={1.2}
            step={0.01}
            value={settings.speed}
            onChange={(speed) => patch({ speed })}
          />
        </>
      )}

      <label className="check">
        <input
          type="checkbox"
          checked={settings.use_speaker_boost}
          onChange={(event) => patch({ use_speaker_boost: event.target.checked })}
        />{" "}
        Speaker boost
      </label>
    </div>
  );
}
